import React, { useState } from "react";
import { Grid, Box, Typography } from "@mui/material";
import { PageHeader } from "components/ui";
import NavigationButtons from "helper/navigation/NavigationButtons";
import DownloadReport from "components/customizedStandardsComponent/DownloadReport";
import StandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";
import ReusableFormComponent from "components/customizedStandardsComponent/EsgFormComponent";
import DownloadPostReviewGapForm from "components/customizedStandardsComponent/DownloadPostReviewGapForm";
import UploadPostReviewGapForm from "components/customizedStandardsComponent/UploadPostReviewGapForm";
import DownloadPostReviewReports from "components/customizedStandardsComponent/DownloadPostReviewReports";
import ReportsReviewDialog from "components/customizedStandardsComponent/ReportsReviewDialog";
import CountrySpecificComplianceSelector from "components/customizedStandardsComponent/CountrySpecificComplianceSelector";
import CountryReportsComponent from "components/customizedStandardsComponent/CountryReportsComponent";

import UploadEvidenceComponent from "components/customizedStandardsComponent/UploadEvidenceComponent";
import UploadEvidenceReviewReports from "components/customizedStandardsComponent/UploadEvidenceReviewReports";

import {
  PROVISIONAL_TERRITORY_SINGAPORE,
  PROVISIONAL_TERRITORY_MALAYSIA,
  PROVISIONAL_TERRITORY_INDONESIA,
  PROVISIONAL_TERRITORY_VIETNAM,
  PROVISIONAL_TERRITORY_THAILAND,
  PROVISIONAL_TERRITORY_PHILIPPINES,
  getAnnexure9,
} from "data/standards/griStandards";

import { COUNTRIES_COMPLIANCE_FORM } from "data/forms/griFormSchema";

import {
  industrailIdentification,
  natureOfActivities,
} from "data/reports/relevantReports";

const COUNTRY_DISPLAY_NAMES = {
  singapore: "Singapore",
  malaysia: "Malaysia",
  indonesia: "Indonesia",
  vietnam: "Vietnam",
  thailand: "Thailand",
  philippines: "Philippines",
};

const CountryAutoSelectAI = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [reportData, setReportData] = useState({
    selectedStandards: [],
    companyInfo: {},
  });
  const [showReviewPopup, setShowReviewPopup] = useState(false);

  const [stepStates, setStepStates] = useState({
    "federal-step": {
      selectedItems: { legislation: [], regulation: [] },
      activeProvince: null,
      activeTab: null,
    },
    "law-regulation-step": {
      selectedItems: { legislation: [], regulation: [] },
      activeProvince: null,
      activeTab: null,
    },
    "federal-laws-step": {
      selectedItems: { legislation: [], regulation: [] },
      activeProvince: null,
      activeTab: null,
    },
  });

  const handleNext = () => {
    setPrevStep(activeStep);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setPrevStep(activeStep);
    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  const handleCountrySelect = (countryCode) => {
    setSelectedCountry(countryCode);
    setActiveStep(3);
  };

  const handleSaveStandards = (selectedStandards) => {
    setReportData((prev) => ({
      ...prev,
      selectedStandards,
    }));
  };

  const handleClosePopup = () => {
    setShowReviewPopup(false);
  };

  const handleSkipToStep5 = () => {
    setPrevStep(activeStep);
    setActiveStep(5);
    setShowReviewPopup(false);
  };

  const handleSelectedItemsChange = (stepId, newSelectedItems) => {
    setStepStates((prev) => ({
      ...prev,
      [stepId]: {
        ...prev[stepId],
        selectedItems: newSelectedItems,
      },
    }));
  };

  const handleActiveProvinceChange = (stepId, activeProvince) => {
    setStepStates((prev) => ({
      ...prev,
      [stepId]: {
        ...prev[stepId],
        activeProvince,
      },
    }));
  };

  const handleActiveTabChange = (stepId, activeTab) => {
    setStepStates((prev) => ({
      ...prev,
      [stepId]: {
        ...prev[stepId],
        activeTab,
      },
    }));
  };

  const renderCountrySpecificContent = () => {
    if (!selectedCountry) {
      return (
        <Typography color="error" align="center" p={4}>
          Please select a country first
        </Typography>
      );
    }

    if (activeStep === 3) {
      const standardsMap = {
        singapore: PROVISIONAL_TERRITORY_SINGAPORE,
        malaysia: PROVISIONAL_TERRITORY_MALAYSIA,
        indonesia: PROVISIONAL_TERRITORY_INDONESIA,
        vietnam: PROVISIONAL_TERRITORY_VIETNAM,
        thailand: PROVISIONAL_TERRITORY_THAILAND,
        philippines: PROVISIONAL_TERRITORY_PHILIPPINES,
      };

      const standards = standardsMap[selectedCountry];

      if (!standards) {
        return (
          <Typography color="error" align="center" p={4}>
            No standards defined for {selectedCountry}
          </Typography>
        );
      }

      return (
        <StandardsSelector
          standards={standards}
          onNext={handleNext}
          onSave={handleSaveStandards}
          title="Select Provincial / Regional / Statutory Requirements from the options below"
          nextButtonText="Next"
          initialSelectedStandards={reportData.selectedStandards.map((s) => s.id)}
        />
      );
    }

    if (activeStep === 4) {
      return (
        <ReusableFormComponent
          key={`form-step-${selectedCountry}`}
          {...COUNTRIES_COMPLIANCE_FORM}
          onNext={handleNext}
          onPrevious={handleBack}
          showPreviousButton={true}
        />
      );
    }

    return null;
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <DownloadReport
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={industrailIdentification}
            showReportCountdownButton={false}
            ReportDownloadButtonShow={false}
            title="Select from the Action Below"
          />
        );

      case 1:
        return (
          <DownloadReport
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={natureOfActivities}
            showReportCountdownButton={false}
            ReportDownloadButtonShow={false}
            title="Select from the Action Below"
          />
        );

      case 2:
        return (
          <CountrySpecificComplianceSelector
            onCountrySelect={handleCountrySelect}
            activeStep={step}
          />
        );

      case 3:
      case 4:
        return renderCountrySpecificContent();

      case 5:
        return (
          <UploadEvidenceComponent
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );

      case 6:
        return (
          <StandardsSelector
            standards={getAnnexure9(
              COUNTRY_DISPLAY_NAMES[selectedCountry] || selectedCountry
            )}
            onNext={handleNext}
            onSave={handleSaveStandards}
            title="Select and download country Specific Reports / Assessments / Analysis"
            nextButtonText="Next"
            initialSelectedStandards={reportData.selectedStandards.map((s) => s.id)}
            showReportCountdownButton={true}
            ReportDownloadButtonShow={true}
          />
        );

      case 7:
        return (
          <UploadEvidenceReviewReports
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );

      case 8:
        return (
          <DownloadPostReviewGapForm
            key="gap-download-step-8"
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );

      case 9:
        return (
          <UploadPostReviewGapForm
            key="gap-upload-step-9"
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );

      case 10:
        return (
          <DownloadPostReviewReports
            key="post-review-download-step-10"
            standardName="Country Specific"
            onPrevious={handleBack}
            showReportCountdownButton={true}
          />
        );

      default:
        return (
          <Typography align="center" color="error" p={4}>
            Unknown step: {step}
          </Typography>
        );
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PageHeader title="AI-Powered ESG Reporting across Global, National, Sectoral, and Industry-Specific Standards" />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <NavigationButtons
              imageKey="dashboardImg4_2"
              title="Country Specific ESG Compliance"
              activeStep={activeStep}
              onBack={handleBack}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          {getStepContent(activeStep)}
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 5,
              mb: 2,
            }}
          >
            {Array.from({ length: 11 }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: index <= activeStep ? "#02462E" : "#e0e0e0",
                  mx: 1,
                }}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <ReportsReviewDialog
        open={showReviewPopup}
        onClose={handleClosePopup}
        onSkip={handleSkipToStep5}
      />
    </>
  );
};

export default CountryAutoSelectAI;